/**
 * PrioritySupportNotificationSettings
 *
 * Sektion im Profil-Tab "Profil" — nur sichtbar, wenn der eingeloggte User
 * Zugang zum Priority-Support-Chat hat (aktiver Plan, Beta-Tester, Admin).
 *
 * Steuert:
 *   - Web-Push-Benachrichtigungen (Account-weit, Toggle)
 *   - Email-Benachrichtigungen   (Account-weit, Toggle)
 *   - Push-Status auf DIESEM Gerät (gerätespezifisch — aktivieren / neu
 *     verbinden / testen / deaktivieren)
 *
 * Synchronisiert mit support.notification_preferences (Account) und
 * support.push_subscriptions (pro Gerät) im Backend.
 */

import React from 'react';
import { Bell, Mail, Smartphone, AlertCircle, CheckCircle2, RefreshCw, Info } from 'lucide-react';
import { useChatAccess } from '../../hooks/useChatAccess';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import { useDevicePushStatus } from '../../hooks/useDevicePushStatus';
import {
  getPushCapability,
  type DevicePushState
} from '../../services/support/pushSubscriptionService';
import TWToggle from '../ui/TWToggle';
import { ProfileSection } from './ProfileSection';
import { NotificationToggleRow } from './NotificationToggleRow';

export const PrioritySupportNotificationSettings: React.FC = () => {
  const access = useChatAccess();
  const enabled = access.hasAccess && !access.isLoading;

  const { preferences, isLoading, error, update } = useNotificationPreferences({ enabled });
  const device = useDevicePushStatus({ enabled });

  // Sektion wird komplett ausgeblendet, wenn User kein Chat-Zugang hat —
  // bewusst keine Hinweis-Card, sonst dient sie unfreiwillig als Werbung
  // für ein Feature das der User noch nicht hat.
  if (!enabled) return null;

  const togglePush = async (next: boolean): Promise<void> => {
    try {
      await update({ webPushEnabled: next });
    } catch {
      /* Hook hat error gesetzt + zurückgerollt */
    }
  };

  const toggleEmail = async (next: boolean): Promise<void> => {
    try {
      await update({ emailEnabled: next });
    } catch {
      /* Hook hat error gesetzt + zurückgerollt */
    }
  };

  // Geräte-Sektion wird nur angezeigt, wenn der Account-Push-Toggle aktiv ist
  // — sonst ist sie wirkungslos und nur verwirrend.
  const showDeviceSection = preferences?.webPushEnabled === true;

  return (
    <ProfileSection
      title="Priority Support — Benachrichtigungen"
      description="Lege fest, wie wir dich erreichen, wenn es eine neue Nachricht im Priority Support gibt."
      layout="stacked"
    >
      {isLoading && !preferences ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Lädt…</p>
      ) : (
        <div className="space-y-4">
          {/* Web-Push */}
          <NotificationToggleRow
            icon={<Bell className="w-4 h-4 text-gray-600 dark:text-gray-300" strokeWidth={2} />}
            title="Web-Push-Benachrichtigungen"
            description={
              <>
                Direkte Benachrichtigung im Browser oder als PWA auf dem Smartphone, wenn du den Chat
                gerade nicht offen hast. Empfehlung für sofortige Reaktion.
              </>
            }
            control={
              <TWToggle
                checked={preferences?.webPushEnabled ?? false}
                onChange={togglePush}
                color="brand"
                label="Web-Push aktivieren"
              />
            }
          >
            {/* Geräte-Status sitzt direkt innerhalb der Web-Push-Reihe, ist also
                visuell als Sub-Block dieser Funktion erkennbar — und verschwindet,
                wenn Push ausgeschaltet ist. */}
            {showDeviceSection && <DeviceStatusBlock device={device} />}
          </NotificationToggleRow>

          {/* Email-Fallback */}
          <NotificationToggleRow
            icon={<Mail className="w-4 h-4 text-gray-600 dark:text-gray-300" strokeWidth={2} />}
            title="Email-Benachrichtigungen"
            description={
              <>
                Falls du nach 5 Minuten noch nicht reagiert hast, schicken wir dir zusätzlich
                eine E-Mail. Inhalt der Nachricht steht aus Datenschutzgründen nicht in der Mail.
              </>
            }
            control={
              <TWToggle
                checked={preferences?.emailEnabled ?? false}
                onChange={toggleEmail}
                color="brand"
                label="Email-Benachrichtigung aktivieren"
              />
            }
          />

          {error && (
            <p className="text-xs text-red-500 mt-2">
              Fehler beim Speichern: {error.message}
            </p>
          )}
        </div>
      )}
    </ProfileSection>
  );
};

// ============================================================================
// DeviceStatusBlock — gerätespezifischer Sub-Block
// ============================================================================

interface DeviceStatusBlockProps {
  device: ReturnType<typeof useDevicePushStatus>;
}

const DeviceStatusBlock: React.FC<DeviceStatusBlockProps> = ({ device }) => {
  const state: DevicePushState | undefined = device.status?.state;

  // Headline + Beschreibungstext je nach State
  const ui = describeState(state, device.isLoading);

  // Android-spezifischer Hinweis: Chrome filtert die erste Push einer
  // unbekannten Quelle als Spam-Verdacht raus, ohne sie sichtbar zuzustellen.
  // Sobald der User in der Chrome-Notification "Kein Spam" tippt, kommen
  // alle weiteren Pushes normal durch. Wir blenden den Hinweis nur ein,
  // wenn die Sub gerade frisch aktiviert wurde (state = active) UND der
  // Browser Android ist — auf iOS gibt es diese Heuristik nicht.
  const cap = getPushCapability();
  const showAndroidSpamHint = cap.isAndroid && state === 'active';

  return (
    <div>
      <div className="flex items-start gap-4 py-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <Smartphone className="w-4 h-4 text-gray-600 dark:text-gray-300" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Status auf diesem Gerät
            </p>
            <StatusPill kind={ui.pillKind} label={ui.pillLabel} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
            {ui.description}
          </p>

          {/* Aktionen */}
          <div className="flex flex-wrap gap-2 mt-3">
            {state === 'inactive' && (
              <ActionButton
                onClick={device.enable}
                disabled={device.isWorking}
                variant="primary"
              >
                {device.isWorking ? 'Wird aktiviert…' : 'Auf diesem Gerät aktivieren'}
              </ActionButton>
            )}
            {state === 'stale' && (
              <ActionButton
                onClick={device.reconnect}
                disabled={device.isWorking}
                variant="primary"
                icon={<RefreshCw className="w-3.5 h-3.5" strokeWidth={2} />}
              >
                {device.isWorking ? 'Verbinde…' : 'Neu verbinden'}
              </ActionButton>
            )}
            {state === 'active' && (
              <>
                <ActionButton
                  onClick={device.sendTestToThisDevice}
                  disabled={device.isWorking}
                  variant="secondary"
                >
                  {device.isWorking ? 'Sendet…' : 'Test-Push senden'}
                </ActionButton>
                <ActionButton
                  onClick={device.disable}
                  disabled={device.isWorking}
                  variant="ghost"
                >
                  Auf diesem Gerät deaktivieren
                </ActionButton>
              </>
            )}
          </div>

          {device.error && (
            <p className="flex items-center gap-1.5 text-xs text-red-500 mt-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
              <span>{device.error}</span>
            </p>
          )}
          {device.message && !device.error && (
            <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 mt-2">
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
              <span>{device.message}</span>
            </p>
          )}

          {showAndroidSpamHint && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-500/5 dark:bg-blue-500/10 ring-1 ring-blue-500/20 px-3 py-2">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" strokeWidth={2} />
              <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                <span className="font-medium">Hinweis für Android:</span> Falls die erste Benachrichtigung
                nicht ankommt, prüfe deine Statusleiste. Chrome blockiert manchmal die erste
                Push-Nachricht einer Seite und fragt dich, ob es Spam ist. Tippe dort auf
                <span className="font-medium"> „Kein Spam"</span>, dann erhältst du ab sofort alle
                Benachrichtigungen zuverlässig.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// State → UI-Texte
// ============================================================================

interface StateUI {
  pillKind: 'on' | 'off' | 'warn';
  pillLabel: string;
  description: string;
}

function describeState(state: DevicePushState | undefined, isLoading: boolean): StateUI {
  if (isLoading || !state) {
    return { pillKind: 'off', pillLabel: 'Lädt…', description: 'Status wird geprüft.' };
  }
  switch (state) {
    case 'active':
      return {
        pillKind: 'on',
        pillLabel: 'Aktiv',
        description:
          'Dieses Gerät erhält Push-Benachrichtigungen. Mit dem Test-Button prüfst du, ob alles ankommt.'
      };
    case 'stale':
      return {
        pillKind: 'warn',
        pillLabel: 'Verbindung verloren',
        description:
          'Die Verbindung zu diesem Gerät ist nicht mehr gültig (kommt nach längerer Inaktivität, OS-Updates oder gelöschten Browser-Daten vor). Bitte einmal neu verbinden.'
      };
    case 'inactive':
      return {
        pillKind: 'off',
        pillLabel: 'Nicht aktiv',
        description:
          'Auf diesem Gerät sind Push-Benachrichtigungen noch nicht aktiviert. Aktiviere sie jetzt, um keine Nachricht zu verpassen.'
      };
    case 'permission-denied':
      return {
        pillKind: 'warn',
        pillLabel: 'Vom Browser blockiert',
        description:
          'Du hast Benachrichtigungen für diese Seite im Browser abgelehnt. Bitte erlaube sie in den Browser-Einstellungen, dann kannst du sie hier aktivieren.'
      };
    case 'ios-needs-pwa':
      return {
        pillKind: 'warn',
        pillLabel: 'iPhone-Hinweis',
        description:
          'Auf dem iPhone funktionieren Push-Benachrichtigungen nur, wenn du die Seite über den Home-Bildschirm öffnest (Teilen → „Zum Home-Bildschirm“).'
      };
    case 'unsupported':
    default:
      return {
        pillKind: 'off',
        pillLabel: 'Nicht unterstützt',
        description:
          'Dieser Browser unterstützt keine Push-Benachrichtigungen. Probiere es mit Chrome, Firefox, Edge oder Safari.'
      };
  }
}

// ============================================================================
// kleine UI-Helfer (lokal, weil sehr spezifisch)
// ============================================================================

const StatusPill: React.FC<{ kind: 'on' | 'off' | 'warn'; label: string }> = ({ kind, label }) => {
  const cls =
    kind === 'on'
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-500/20'
      : kind === 'warn'
        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-1 ring-amber-500/20'
        : 'bg-gray-500/10 text-gray-600 dark:text-gray-400 ring-1 ring-gray-500/20';
  return (
    <span className={`inline-flex items-center text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>
      {label}
    </span>
  );
};

interface ActionButtonProps {
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  variant: 'primary' | 'secondary' | 'ghost';
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const ActionButton: React.FC<ActionButtonProps> = ({ onClick, disabled, variant, icon, children }) => {
  const base = 'inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed';
  const styles =
    variant === 'primary'
      ? 'bg-brand text-gray-950 font-semibold hover:bg-brand-hover'
      : variant === 'secondary'
        ? 'border border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200';
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={disabled}
      className={`${base} ${styles}`}
    >
      {icon}
      {children}
    </button>
  );
};

export default PrioritySupportNotificationSettings;
